import React, { useState } from "react";
import { Box, Typography, IconButton, Switch, Divider, Button, ButtonGroup } from "@mui/material";
import { Add, ModeEditOutlineTwoTone, Visibility } from "@mui/icons-material";
import { toast } from "react-toastify";
import FormDrawer from "components/Shared/FormDrawer";
import DocumentForm from "../../Document/DocumentForm";
import { useAuth } from "hooks";
import {
  useAddDocumentSourceMutation,
  useGetDocumentsBySourceQuery,
  useUpdateDocumentByIdMutation,
} from "store/services/documentApi";
import RecordCard from "./RecordCard";

const FieldRow = ({ label, children, value }) => (
  <Box sx={{ display: "flex", py: 0.25, alignItems: "center" }}>
    <Box sx={{ width: "40%", color: "text.primary", fontSize: "0.7rem", fontWeight: 600 }}>{label}</Box>
    <Box sx={{ width: "60%", fontSize: "0.74rem", fontWeight: 500 }}>
      {children ? children : value ?? "N/A"}
    </Box>
  </Box>
);

const DocumentRecordCard = ({ doc, canEdit, isAccountAdmin, isActive, onEdit, onToggleActive, updatingDocument }) => (
  <RecordCard>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
<Typography sx={{ fontSize: "0.78rem", fontWeight: 700 }} className="line-clamp-2" title={doc?.title}>
        {doc?.title || "N/A"}
      </Typography>
      {(!canEdit || isAccountAdmin) && (
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {!canEdit && (
            <Switch
              checked={doc?.isActive || false}
              onChange={() => onToggleActive(doc)}
              size="small"
              disabled={updatingDocument || isActive === false}
            />
          )}
          {isAccountAdmin && (
            <IconButton size="small" onClick={() => onEdit(doc)} disabled={isActive === false}>
              <ModeEditOutlineTwoTone sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
    <Divider sx={{ my: 0.5 }} />
    {(doc?.vendorDocumentType?.name || doc?.documentType?.name) && (
      <FieldRow
        label="Document Type"
        value={doc?.vendorDocumentType?.name || doc?.documentType?.name}
      />
    )}
    <FieldRow label="Created By" value={doc?.createdBy?.name} />
    <FieldRow label="Created At" value={doc?.createdAt ? new Date(doc.createdAt).toLocaleString() : "N/A"} />
    {doc?.updatedBy?.name && <FieldRow label="Updated By" value={doc.updatedBy.name} />}
    {doc?.updatedBy && doc?.updatedAt && (
      <FieldRow label="Updated At" value={new Date(doc.updatedAt).toLocaleString()} />
    )}
    <FieldRow label="Attachments">
      {doc?.attachments?.length > 0 ? (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {doc.attachments.map((att) => (
            <IconButton key={att?._id} size="small" onClick={() => window.open(att?.url, "_blank")}>
              <Visibility sx={{ fontSize: "1rem" }} color="primary" />
            </IconButton>
          ))}
        </Box>
      ) : (
        "No attachments"
      )}
    </FieldRow>
  </RecordCard>
);

const ShipmentDocumentsCompact = ({ source, sourceRef, isActive }) => {
  const { isCustomer, isVendor, isContractor, isDriver, isAccountAdmin } = useAuth();
  const canEdit = isCustomer || isVendor || isContractor || isDriver;

  const [activeDocTab, setActiveDocTab] = useState("Active");
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const { data: documents, isLoading: loadingDocuments } = useGetDocumentsBySourceQuery({
    source,
    sourceRef,
    active: activeDocTab === "Active" ? "true" : "false",
  });

  const [addDocumentSource, { isLoading: addingDoc }] = useAddDocumentSourceMutation();
  const [updateDocumentById, { isLoading: updatingDocument }] = useUpdateDocumentByIdMutation();

  const handleCancelDocument = () => {
    setShowDocumentForm(false);
    setSelectedDocument(null);
  };

  const handleAddDocument = async (data) => {
    try {
      await addDocumentSource({ ...data, source, sourceRef }).unwrap();
      toast.success("Document successfully added!");
      handleCancelDocument();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't add document!");
    }
  };

  const handleEditDocument = (document) => {
    setSelectedDocument(document);
    setShowDocumentForm(true);
  };

  const handleUpdateDocument = async (data) => {
    const body = {
      _id: selectedDocument?._id,
      title: data.title,
      attachments: data.attachments,
      source,
      vendorDocumentType: data.vendorDocumentType,
      documentType: data.documentType,
      sourceRef,
      isActive: data.isActive,
      expiryDate: data.hasExpiry ? data.expiryDate : null,
    };
    try {
      await updateDocumentById({ body }).unwrap();
      toast.success("Document successfully updated!");
      handleCancelDocument();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update document!");
    }
  };

  const handleToggleActive = async (document) => {
    const body = {
      _id: document?._id,
      title: document?.title,
      attachments: document?.attachments || [],
      source: document?.source,
      vendorDocumentType: document?.vendorDocumentType,
      documentType: document?.documentType,
      sourceRef: document?.sourceRef,
      isActive: !document?.isActive,
    };
    try {
      await updateDocumentById({ body }).unwrap();
      toast.success("Document status updated successfully!");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update document status!");
    }
  };

  const docList = Array.isArray(documents?.data) ? documents.data : [];

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <ButtonGroup size="small">
          <Button
            variant={activeDocTab === "Active" ? "contained" : "outlined"}
            onClick={() => setActiveDocTab("Active")}
            sx={{ fontSize: "0.65rem", py: 0.25 }}
          >
            Active
          </Button>
          <Button
            variant={activeDocTab === "Inactive" ? "contained" : "outlined"}
            onClick={() => setActiveDocTab("Inactive")}
            sx={{ fontSize: "0.65rem", py: 0.25 }}
          >
            Inactive
          </Button>
        </ButtonGroup>
        {!canEdit && (
          <IconButton
            size="small"
            disabled={isActive === false}
            onClick={() => { setSelectedDocument(null); setShowDocumentForm(true); }}
          >
            <Add sx={{ fontSize: "1rem" }} />
          </IconButton>
        )}
      </Box>
      {loadingDocuments ? (
        <Typography sx={{ fontSize: "0.74rem", color: "text.secondary", textAlign: "center", py: 2 }}>
          Loading documents...
        </Typography>
      ) : docList.length === 0 ? (
        <Typography sx={{ fontSize: "0.74rem", color: "text.secondary", textAlign: "center", py: 2 }}>
          No documents found.
        </Typography>
      ) : (
        docList.map((doc) => (
          <DocumentRecordCard
            key={doc._id}
            doc={doc}
            canEdit={canEdit}
            isAccountAdmin={isAccountAdmin}
            isActive={isActive}
            onEdit={handleEditDocument}
            onToggleActive={handleToggleActive}
            updatingDocument={updatingDocument}
          />
        ))
      )}
      <FormDrawer title="Document" open={showDocumentForm} onClose={handleCancelDocument}>
        <DocumentForm
          isBusy={updatingDocument || addingDoc}
          editDocumentData={selectedDocument}
          onSubmit={(data) => (selectedDocument ? handleUpdateDocument(data) : handleAddDocument(data))}
          onCancel={handleCancelDocument}
        />
      </FormDrawer>
    </Box>
  );
};

export default ShipmentDocumentsCompact;